import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { getSignableResourceTypeList } from "../sharedCode/queries";
import { SignableResourceType } from "../sharedCode/avroGraphql";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";

export const CUSTOMER_SIGNATURE = 2;
export const PASSENGER_SIGNATURE = 3;
export const DANGEROUS_GOODS_INITIALS = 4;

export default function useSignableResourceTypeBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getSignableResourceTypeList, {
        fetchPolicy: "cache-and-network",
    });

    const fetchData = (firstRun: boolean) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getSignableResourceTypes.slice() as SignableResourceType[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: SignableResourceType[]) {
        TicketDatabase.transaction("rw", TicketDatabase.signableResourceType, async () => {
            await TicketDatabase.signableResourceType
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.signableResourceType.bulkDelete(keys);
                })
                .then(() => TicketDatabase.signableResourceType.bulkAdd(rawList));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = ObjectUtils.convertToUppercase(gqlResult.getSignableResourceTypes.slice()) as SignableResourceType[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.signableResourceType.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
